// Voxel-aware коллизия игрока против стен на клиенте (зеркало server).
// Использует ChunkRegistry для доступа к alive bitmask чанков и
// tilesDamaged set чтобы знать какие тайлы требуют voxel-direct check.

import type { GridMap, MeshCollisionEntry } from "./world";
import { wallCellCylinderHitsAnyZSpan, WALL_GRID_Z_SPAN_STRIDE, MESH_WALL_AABB_MIN_HEIGHT } from "./world";
import { worldToCell, tileWorldBounds } from "./world";
import type { ChunkRegistry, VoxelChunkClient } from "./chunks";
import { ChunkRole } from "../net/protocol";
import { COLUMN_EMPTY_TOP, COLUMN_EMPTY_BOTTOM, ensureColumnMaps } from "./chunks";
import { getTerrainHeightmap, terrainFloorAt, TERRAIN_EMPTY_TOP } from "./terrainHeightmap";

/** mesh_collision из level-manifest (GLB AABB). */
let meshCollisionBoxes: MeshCollisionEntry[] | null = null;

/** Декодированные stair_tris (raycast по треугольникам) — параллельный массив
 *  той же длины, что meshCollisionBoxes. null если у записи нет stair_tris. */
interface StairTrisDecoded {
  ox: number;
  oy: number;
  cell: number;
  nx: number;
  ny: number;
  verts: Float32Array;       // 9 floats per tri (a.xyz, b.xyz, c.xyz)
  binOffsets: Uint32Array;   // length nx*ny + 1
  binIndices: Uint32Array;
}
let stairTrisDecoded: (StairTrisDecoded | null)[] | null = null;

function decodeBase64ToBytes(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function decodeStairTris(entry: MeshCollisionEntry): StairTrisDecoded | null {
  const t = entry.stair_tris;
  if (!t || t.nx <= 0 || t.ny <= 0 || t.cell <= 1e-8 || t.tri_count <= 0) return null;
  try {
    const vbytes = decodeBase64ToBytes(t.verts_b64);
    const obytes = decodeBase64ToBytes(t.bin_offsets_b64);
    const ibytes = decodeBase64ToBytes(t.bin_indices_b64);
    if (vbytes.byteLength !== t.tri_count * 9 * 4) return null;
    if (obytes.byteLength !== (t.nx * t.ny + 1) * 4) return null;
    if (ibytes.byteLength % 4 !== 0) return null;
    return {
      ox: t.ox, oy: t.oy, cell: t.cell, nx: t.nx, ny: t.ny,
      verts: new Float32Array(vbytes.buffer, vbytes.byteOffset, t.tri_count * 9),
      binOffsets: new Uint32Array(obytes.buffer, obytes.byteOffset, t.nx * t.ny + 1),
      binIndices: new Uint32Array(ibytes.buffer, ibytes.byteOffset, ibytes.byteLength / 4),
    };
  } catch {
    return null;
  }
}

/** Проём в сетке стен (большой wall_* AABB), зеркало mansionDoorCarve* на сервере. */
let mansionDoorGridCarve:
  | { minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number }
  | null = null;

/** Зеркало server mansion_door.go: узкий carve только по фактическому дверному проёму. */
const MANSION_GRID_CARVE_PAD_X = 0.18;
const MANSION_GRID_CARVE_PAD_NEG_Y = 0.45;
const MANSION_GRID_CARVE_PAD_POS_Y = 0.25;
const MANSION_GRID_CARVE_Z_PAD_DOWN = 0.35;
const MANSION_GRID_CARVE_Z_PAD_UP = 0.75;

function recomputeMansionDoorGridCarve(): void {
  mansionDoorGridCarve = null;
  if (!meshCollisionBoxes) return;
  let left: MeshCollisionEntry | null = null;
  let right: MeshCollisionEntry | null = null;
  for (const box of meshCollisionBoxes) {
    if (box.role !== "door") continue;
    const n = (box.object_name ?? "").toLowerCase();
    if (n.includes("door_left_manison") || n.includes("door_left_mansion")) left = box;
    if (n.includes("door_right_manison") || n.includes("door_right_mansion")) right = box;
  }
  if (!left || !right) return;
  const minX = Math.min(left.min[0], right.min[0]) - MANSION_GRID_CARVE_PAD_X;
  const maxX = Math.max(left.max[0], right.max[0]) + MANSION_GRID_CARVE_PAD_X;
  const minY = Math.min(left.min[1], right.min[1]) - MANSION_GRID_CARVE_PAD_NEG_Y;
  const maxY = Math.max(left.max[1], right.max[1]) + MANSION_GRID_CARVE_PAD_POS_Y;
  const minZ = Math.min(left.min[2], right.min[2]) - MANSION_GRID_CARVE_Z_PAD_DOWN;
  const maxZ = Math.max(left.max[2], right.max[2]) + MANSION_GRID_CARVE_Z_PAD_UP;
  mansionDoorGridCarve = { minX, maxX, minY, maxY, minZ, maxZ };
}

export function setMeshCollision(boxes: MeshCollisionEntry[] | null): void {
  // HF stair из level.json — как на сервере после loadScene: без box-blur,
  // чтобы Z тредов совпадали с растером build_level (blur занижал опору).
  meshCollisionBoxes = boxes?.length ? boxes : null;
  if (meshCollisionBoxes) {
    stairTrisDecoded = meshCollisionBoxes.map((b) =>
      b.role === "stair" ? decodeStairTris(b) : null,
    );
  } else {
    stairTrisDecoded = null;
  }
  recomputeMansionDoorGridCarve();
  recomputeInteriorDoorMeta();
}

// ─── Межкомнатные двери (mesh_collision role=door, кроме парадной) ─────
// Зеркало server interior_doors.go: рамы/петли/пороги проход не блокируют
// никогда (их AABB накрывает весь проём/порожек), полотна (+ручки) блокируют
// пока дверь не открыта (ghost по базе имени).
let interiorDoorLeafBaseByBox: (string | null)[] | null = null;
let interiorDoorPassthroughByBox: boolean[] | null = null;
let interiorDoorGhostBases = new Set<string>();
let interiorDoorHotspots: { base: string; hx: number; hy: number; minZ: number; maxZ: number }[] = [];

function interiorDoorBoxMeta(objectName: string | undefined): { base: string; part: 1 | 2 } | null {
  if (!objectName) return null;
  const n = objectName.toLowerCase().trim();
  if (
    n.includes("door_left_manison") || n.includes("door_left_mansion") ||
    n.includes("door_right_manison") || n.includes("door_right_mansion")
  ) {
    return null; // парадная — свой runtime
  }
  for (const sfx of ["_leafl", "_leafr", "_leaf", "_handlel", "_handler", "_handle"]) {
    if (n.endsWith(sfx)) return { base: n.slice(0, -sfx.length), part: 1 };
  }
  for (const sfx of ["_frame", "_hinges", "_threshold"]) {
    if (n.endsWith(sfx)) return { base: n.slice(0, -sfx.length), part: 2 };
  }
  return null;
}

function recomputeInteriorDoorMeta(): void {
  interiorDoorLeafBaseByBox = null;
  interiorDoorPassthroughByBox = null;
  interiorDoorGhostBases = new Set();
  interiorDoorHotspots = [];
  if (!meshCollisionBoxes) return;
  const leafBase: (string | null)[] = new Array(meshCollisionBoxes.length).fill(null);
  const pass: boolean[] = new Array(meshCollisionBoxes.length).fill(false);
  const agg = new Map<string, { minX: number; minY: number; maxX: number; maxY: number; minZ: number; maxZ: number }>();
  for (let i = 0; i < meshCollisionBoxes.length; i++) {
    const box = meshCollisionBoxes[i];
    if (box.role !== "door") continue;
    const meta = interiorDoorBoxMeta(box.object_name);
    if (!meta) continue;
    if (meta.part === 2) {
      pass[i] = true;
      continue;
    }
    leafBase[i] = meta.base;
    const a = agg.get(meta.base);
    if (!a) {
      agg.set(meta.base, {
        minX: box.min[0], minY: box.min[1], maxX: box.max[0], maxY: box.max[1],
        minZ: box.min[2], maxZ: box.max[2],
      });
    } else {
      a.minX = Math.min(a.minX, box.min[0]);
      a.minY = Math.min(a.minY, box.min[1]);
      a.maxX = Math.max(a.maxX, box.max[0]);
      a.maxY = Math.max(a.maxY, box.max[1]);
      a.minZ = Math.min(a.minZ, box.min[2]);
      a.maxZ = Math.max(a.maxZ, box.max[2]);
    }
  }
  interiorDoorLeafBaseByBox = leafBase;
  interiorDoorPassthroughByBox = pass;
  interiorDoorHotspots = [...agg.entries()]
    .map(([base, a]) => ({
      base,
      hx: (a.minX + a.maxX) * 0.5,
      hy: (a.minY + a.maxY) * 0.5,
      minZ: a.minZ,
      maxZ: a.maxZ,
    }))
    .sort((a, b) => (a.base < b.base ? -1 : a.base > b.base ? 1 : 0));
}

/** Отсортированные базы имён межкомнатных дверей — ТОТ ЖЕ список, что строит
 *  сервер из mesh_collision (interior_doors.go). Индексы EvInteriorDoor
 *  ссылаются на этот порядок; клиентский реестр анимаций обязан строиться
 *  от него, а не от нод glb (нода-полотно может отсутствовать в glb —
 *  например скрыта из рендера — и списки разъезжались: E открывал соседнюю
 *  дверь). */
export function getInteriorDoorBases(): string[] {
  return interiorDoorHotspots.map((h) => h.base);
}

export function setInteriorDoorGhostBase(base: string, on: boolean): void {
  if (on) interiorDoorGhostBases.add(base);
  else interiorDoorGhostBases.delete(base);
}

/** Игрок в зоне взаимодействия с какой-либо межкомнатной дверью (подсказка E). */
const INTERIOR_DOOR_USE_RADIUS = 1.2; // зеркало server interiorDoorTriggerRadius

export function feetNearInteriorDoorInteract(px: number, py: number, feetZ: number): boolean {
  for (const h of interiorDoorHotspots) {
    const dx = px - h.hx;
    const dy = py - h.hy;
    if (dx * dx + dy * dy > INTERIOR_DOOR_USE_RADIUS * INTERIOR_DOOR_USE_RADIUS) continue;
    if (feetZ < h.minZ - MANSION_DOOR_FEET_Z_PAD_DOWN || feetZ > h.maxZ + 0.75) continue;
    return true;
  }
  return false;
}

/** Дверь особняка GLB-only: ghost-коллизия без chunk id (зеркало mansionDoorGhost на сервере). */
let mansionDoorMeshGhost = false;

export function setMansionDoorMeshGhost(on: boolean): void {
  mansionDoorMeshGhost = on;
}

/** Чанки door_* с отключённой коллизией (дверь открыта) — зеркало сервера PlayerGhost. */
let mansionDoorGhostIds: Set<number> | null = null;

export function setMansionDoorGhostChunks(ids: Set<number> | null): void {
  mansionDoorGhostIds = ids?.size ? ids : null;
}

/** Допуск на d² в circle-vs-AABB (сервер: circleAABBEpsilonSq), граница касания. */
const CIRCLE_AABB_EPS_SQ = 1e-5;

/** Режим «уже внутри» (зеркало server overlapExempt, см. moveAndSlideVoxel):
 *  примитив коллизии не преграда, если цилиндр в опорной точке (x,y) — откуда
 *  начат шаг, на тех же высоте и радиусе — его УЖЕ пересекает. Примитив:
 *  AABB mesh_collision, воксельная дверь и предмет декора — целиком, куб стены —
 *  по одному; в бокс к тому же нельзя углубляться (exemptCoversBox). Из бокса
 *  глобуса, в который упал сверху, выйти можно; влезть в НОВЫЙ куб/бокс (шкаф
 *  рядом) — нет. undefined — обычная проверка. */
export interface OverlapExempt {
  x: number;
  y: number;
}

function exemptCovers(
  ex: OverlapExempt | undefined, r: number, x0: number, y0: number, x1: number, y1: number,
): boolean {
  return ex !== undefined && circleVsAABB(ex.x, ex.y, r, x0, y0, x1, y1);
}

/** exemptCovers для AABB mesh_collision плюс «не глубже» (зеркало server
 *  coversBox): выйти и скользить вдоль грани можно, тонкий бокс (окно,
 *  полотно двери) насквозь — нет. Без допуска: вдоль грани глубина та же. */
function exemptCoversBox(
  ex: OverlapExempt | undefined, x: number, y: number, r: number,
  x0: number, y0: number, x1: number, y1: number,
): boolean {
  if (!exemptCovers(ex, r, x0, y0, x1, y1)) return false;
  return boxPenetration(x, y, r, x0, y0, x1, y1) <= boxPenetration(ex!.x, ex!.y, r, x0, y0, x1, y1);
}

/** Глубина круга в XY-прямоугольнике: r минус знаковое расстояние от центра
 *  (снаружи — до ближайшей точки, внутри — минус до ближайшей грани).
 *  Зеркало server boxPenetration. */
function boxPenetration(cx: number, cy: number, r: number, x0: number, y0: number, x1: number, y1: number): number {
  if (cx >= x0 && cx <= x1 && cy >= y0 && cy <= y1) {
    return r + Math.min(cx - x0, x1 - cx, cy - y0, y1 - cy);
  }
  const dx = cx - Math.min(Math.max(cx, x0), x1);
  const dy = cy - Math.min(Math.max(cy, y0), y1);
  return r - Math.sqrt(dx * dx + dy * dy);
}

/** Высота цилиндра-коллайдера игрока стоя (зеркало server PlayerColliderHeight). */
export const PLAYER_COLLIDER_HEIGHT = 1.8;
/** В приседе — низкий коллайдер, чтобы пролезать под балками. */
export const PLAYER_COLLIDER_HEIGHT_CROUCH = 1.0;

/** Цилиндр игрока пересекает alive-кубы воксельного чанка (общий хелпер
 *  для дверей RoleDoor; ранее использовался и для stair, теперь Doom-2-стиль). */
function voxelCylinderHitsChunk(
  c: VoxelChunkClient,
  x: number, y: number, feetZ: number, r: number, colliderH: number,
): boolean {
  const headZ = feetZ + colliderH;
  const cs = c.cubeSize;
  const chWx1 = c.ox + c.sx * cs;
  const chWy1 = c.oy + c.sy * cs;
  if (!circleVsAABB(x, y, r, c.ox, c.oy, chWx1, chWy1)) return false;
  const chZ1 = c.oz + c.sz * cs;
  if (headZ <= c.oz || feetZ >= chZ1) return false;
  const sx = c.sx;
  const sy = c.sy;
  const sz = c.sz;
  const sxsy = sx * sy;
  let bxMin = Math.floor((x - r - c.ox) / cs) - 1;
  let bxMax = Math.floor((x + r - c.ox) / cs) + 1;
  let byMin = Math.floor((y - r - c.oy) / cs) - 1;
  let byMax = Math.floor((y + r - c.oy) / cs) + 1;
  if (bxMin < 0) bxMin = 0;
  if (byMin < 0) byMin = 0;
  if (bxMax >= sx) bxMax = sx - 1;
  if (byMax >= sy) byMax = sy - 1;
  if (bxMin > bxMax || byMin > byMax) return false;
  let bzMin = Math.floor((feetZ - c.oz) / cs) - 1;
  let bzMax = Math.floor((headZ - c.oz) / cs) + 1;
  if (bzMin < 0) bzMin = 0;
  if (bzMax >= sz) bzMax = sz - 1;
  if (bzMin > bzMax) return false;
  const half = cs * 0.5;
  for (let bz = bzMin; bz <= bzMax; bz++) {
    const zLo = c.oz + bz * cs;
    const zHi = zLo + cs;
    if (zHi <= feetZ || zLo >= headZ) continue;
    const zBase = bz * sxsy;
    for (let by = byMin; by <= byMax; by++) {
      const yBase = by * sx;
      for (let bx = bxMin; bx <= bxMax; bx++) {
        const idx = bx + yBase + zBase;
        if ((c.alive[idx >> 3] & (1 << (idx & 7))) === 0) continue;
        const cx = c.ox + (bx + 0.5) * cs;
        const cy = c.oy + (by + 0.5) * cs;
        if (circleVsAABB(x, y, r, cx - half, cy - half, cx + half, cy + half)) return true;
      }
    }
  }
  return false;
}

/** Doom-2-стиль: Z stair в точке (cx,cy) — одна «вертикаль», без max по диску.
 *  Воксельный stair: колонка под центром; mesh stair: HF/planar в той же точке
 *  (см. stairMeshFootSupportZ). r сохранён в API для совместимости. */
export function stairSurfaceTopAt(
  chunks: ChunkRegistry, cx: number, cy: number, r: number,
): number {
  let best = -Infinity;
  for (const c of chunks.all()) {
    if (c.dynamic || c.role !== ChunkRole.Stair) continue;
    const cs = c.cubeSize;
    const sx = c.sx;
    const sy = c.sy;
    const chWxEnd = c.ox + sx * cs;
    const chWyEnd = c.oy + sy * cs;
    if (cx < c.ox || cy < c.oy || cx >= chWxEnd || cy >= chWyEnd) continue;
    ensureColumnMaps(c);
    if (c.columnTopZ === null) continue;
    const bx = Math.floor((cx - c.ox) / cs);
    const by = Math.floor((cy - c.oy) / cs);
    if (bx < 0 || by < 0 || bx >= sx || by >= sy) continue;
    const v = c.columnTopZ[bx + by * sx];
    if (v === COLUMN_EMPTY_TOP) continue;
    if (v > best) best = v;
  }
  if (meshCollisionBoxes) {
    for (let i = 0; i < meshCollisionBoxes.length; i++) {
      const box = meshCollisionBoxes[i];
      if (box.role !== "stair") continue;
      if (cx < box.min[0] || cy < box.min[1] || cx > box.max[0] || cy > box.max[1]) {
        continue;
      }
      const sz = stairMeshFootSupportZ(cx, cy, r, box, i);
      if (sz > best) best = sz;
    }
  }
  return best;
}

function playerCollidesDoorVoxels(
  chunks: ChunkRegistry,
  x: number, y: number, feetZ: number, r: number, colliderH: number,
  ex?: OverlapExempt,
): boolean {
  for (const c of chunks.all()) {
    if (c.role !== ChunkRole.Door) continue;
    if (mansionDoorGhostIds?.has(c.id)) continue;
    if (voxelCylinderHitsChunk(c, x, y, feetZ, r, colliderH)) {
      // «Уже внутри» — дверь целиком (закрылась на стоящего в проёме).
      if (ex && voxelCylinderHitsChunk(c, ex.x, ex.y, feetZ, r, colliderH)) continue;
      return true;
    }
  }
  if (meshCollisionBoxes) {
    for (let i = 0; i < meshCollisionBoxes.length; i++) {
      const box = meshCollisionBoxes[i];
      if (box.role !== "door") continue;
      if (mansionDoorMeshGhost && isNamedMansionDoorPair(box.object_name)) continue;
      // Межкомнатные: рамы/петли/пороги не блокируют никогда, полотна
      // открытой двери — пока её база в ghost-set (зеркало сервера).
      if (interiorDoorPassthroughByBox?.[i]) continue;
      const leafBase = interiorDoorLeafBaseByBox?.[i];
      if (leafBase && interiorDoorGhostBases.has(leafBase)) continue;
      if (
        cylinderOverlapsVerticalAABB(
          x, y, feetZ, r, colliderH,
          box.min[0], box.min[1], box.min[2], box.max[0], box.max[1], box.max[2],
        )
      ) {
        if (exemptCoversBox(ex, x, y, r, box.min[0], box.min[1], box.max[0], box.max[1])) continue;
        return true;
      }
    }
  }
  return false;
}

function mansionDoorCarveSkipsGridWallBlock(
  x: number, y: number, feetZ: number, headZ: number, r: number,
): boolean {
  if (!mansionDoorMeshGhost || !mansionDoorGridCarve) return false;
  const c = mansionDoorGridCarve;
  if (headZ <= c.minZ || feetZ >= c.maxZ) return false;
  return circleVsAABB(x, y, r, c.minX, c.minY, c.maxX, c.maxY);
}

function isNamedMansionDoorPair(objectName: string | undefined): boolean {
  if (!objectName) return false;
  const n = objectName.toLowerCase();
  return (
    n.includes("door_left_manison") ||
    n.includes("door_left_mansion") ||
    n.includes("door_right_manison") ||
    n.includes("door_right_mansion")
  );
}

/** Радиус триггера Use — как `mansionDoorTriggerRadius` на сервере. */
const MANSION_DOOR_USE_RADIUS = 1.4;
/** Ноги у порога vs центр высокого AABB двери: допуск по Z от min/max bbox дверей. */
const MANSION_DOOR_FEET_Z_PAD_DOWN = 1.35;
/** mesh_collision двери: пол внутри дома сильно выше AABB полотен — широкий допуск вверх. */
const MANSION_DOOR_FEET_Z_PAD_UP = 48;

/**
 * Игрок в зоне взаимодействия с парной дверью особняка (mesh_collision).
 * Нельзя сравнивать только с центром Z из Hello — у высокой двери центр по Z
 * сильно выше ступней игрока.
 */
export function feetNearMansionDoorInteract(px: number, py: number, feetZ: number): boolean {
  if (!meshCollisionBoxes) return false;
  let left: MeshCollisionEntry | null = null;
  let right: MeshCollisionEntry | null = null;
  for (const box of meshCollisionBoxes) {
    if (box.role !== "door") continue;
    const n = (box.object_name ?? "").toLowerCase();
    if (n.includes("door_left_manison") || n.includes("door_left_mansion")) left = box;
    if (n.includes("door_right_manison") || n.includes("door_right_mansion")) right = box;
  }
  if (!left || !right) return false;
  const cxL = (left.min[0] + left.max[0]) * 0.5;
  const cyL = (left.min[1] + left.max[1]) * 0.5;
  const cxR = (right.min[0] + right.max[0]) * 0.5;
  const cyR = (right.min[1] + right.max[1]) * 0.5;
  const hx = (cxL + cxR) * 0.5;
  const hy = (cyL + cyR) * 0.5;
  const dx = px - hx;
  const dy = py - hy;
  if (dx * dx + dy * dy > MANSION_DOOR_USE_RADIUS * MANSION_DOOR_USE_RADIUS) return false;
  const minZ = Math.min(left.min[2], right.min[2]);
  const maxZ = Math.max(left.max[2], right.max[2]);
  return (
    feetZ >= minZ - MANSION_DOOR_FEET_Z_PAD_DOWN &&
    feetZ <= maxZ + MANSION_DOOR_FEET_Z_PAD_UP
  );
}

function playerCollidesMeshWallAABBs(
  x: number, y: number, feetZ: number, r: number, colliderH: number,
  ex?: OverlapExempt,
): boolean {
  if (!meshCollisionBoxes) return false;
  for (const box of meshCollisionBoxes) {
    const role = box.role;
    if (role !== "wall" && role !== "window") continue;
    // Зеркало server playerCollidesMeshWallAABBs: вырожденные по высоте боксы
    // (плоская «крышка» фундамента wall_fundament~X_Y) — пол, а не стена.
    if (box.max[2] - box.min[2] < MESH_WALL_AABB_MIN_HEIGHT) continue;
    if (
      cylinderOverlapsVerticalAABB(
        x, y, feetZ, r, colliderH,
        box.min[0], box.min[1], box.min[2], box.max[0], box.max[1], box.max[2],
      )
    ) {
      if (exemptCoversBox(ex, x, y, r, box.min[0], box.min[1], box.max[0], box.max[1])) continue;
      return true;
    }
  }
  return false;
}

/** Коллизия круга против стен с учётом разрушенных воксел и закрытых дверей.
 *  feetZ / colliderH — мировая высота ступней и высота цилиндра (как на сервере).
 *
 *  ВАЖНО: Лестницы (RoleStair) в этой проверке НЕ участвуют (Doom-2-стиль) —
 *  см. `stairSurfaceTopAt` и `moveAndSlideVoxel`. Иначе цилиндр игрока,
 *  стоящего на лестнице, сталкивался бы с её AABB и блокировал ход вниз. */
export function playerCollidesWalls(
  map: GridMap,
  chunks: ChunkRegistry,
  damaged: Set<number>,
  x: number, y: number, feetZ: number, r: number, colliderH: number,
  /** Режим «уже внутри»: задетое в ex не блокирует, клетки сетки — только
   *  точно (воксели + mesh-AABB), как server playerCollidesWallsEx. */
  ex?: OverlapExempt,
): boolean {
  const corners: [number, number][] = [[x - r, y - r], [x + r, y - r], [x - r, y + r], [x + r, y + r]];
  let txMin = map.width;
  let txMax = -1;
  let tyMin = map.height;
  let tyMax = -1;
  for (const [wx, wy] of corners) {
    const [tx, ty] = worldToCell(map, wx, wy);
    if (tx < txMin) txMin = tx;
    if (tx > txMax) txMax = tx;
    if (ty < tyMin) tyMin = ty;
    if (ty > tyMax) tyMax = ty;
  }
  const headZ = feetZ + colliderH;
  const cellWallZSpans = map.cellWallZSpans;
  const hasZRange =
    !!cellWallZSpans &&
    cellWallZSpans.length === map.width * map.height * WALL_GRID_Z_SPAN_STRIDE;
  for (let ty = tyMin; ty <= tyMax; ty++) {
    for (let tx = txMin; tx <= txMax; tx++) {
      if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) {
        return true;
      }
      const tileIdx = tx + ty * map.width;
      const tileDamaged = damaged.has(tileIdx);
      if (map.cells[tileIdx] === 0 && !tileDamaged) continue; // открытое пространство
      const [x0, y0, x1, y1] = tileWorldBounds(map, tx, ty);
      if (!tileDamaged && !ex) {
        // Fast path: circle vs tile bbox + Z-фильтр (зеркало server). Без
        // Z-фильтра mesh_collision стена с высоким min_z (балка над крыльцом)
        // блокировала бы игрока на полу — тайл solid, но реальный AABB вверху.
        if (!circleVsAABB(x, y, r, x0, y0, x1, y1)) continue;
        if (hasZRange) {
          if (!wallCellCylinderHitsAnyZSpan(cellWallZSpans!, tileIdx, feetZ, headZ)) {
            continue;
          }
          if (mansionDoorCarveSkipsGridWallBlock(x, y, feetZ, headZ, r)) continue;
          return true;
        }
        if (mansionDoorCarveSkipsGridWallBlock(x, y, feetZ, headZ, r)) continue;
        return true;
      }
      // Slow path (damaged или режим «уже внутри»): voxel-direct
      if (voxelCollideTile(chunks, map, tx, ty, x, y, r, feetZ, colliderH, ex)) return true;
    }
  }
  return (
    playerCollidesDoorVoxels(chunks, x, y, feetZ, r, colliderH, ex) ||
    playerCollidesMeshWallAABBs(x, y, feetZ, r, colliderH, ex)
  );
}

function voxelCollideTile(
  chunks: ChunkRegistry,
  map: GridMap,
  tx: number, ty: number,
  x: number, y: number, r: number,
  feetZ: number, colliderH: number,
  ex?: OverlapExempt,
): boolean {
  const headZ = feetZ + colliderH;
  const [x0t, y0t, x1t, y1t] = tileWorldBounds(map, tx, ty);
  for (const c of chunks.all()) {
    if (c.role !== ChunkRole.Wall) continue;
    const [chTxBase, chTyBase] = worldToCell(map, c.ox, c.oy);
    const chWxEnd = c.ox + c.sx * c.cubeSize;
    const chWyEnd = c.oy + c.sy * c.cubeSize;
    const [chTxEnd, chTyEnd] = worldToCell(map, chWxEnd - 1e-3, chWyEnd - 1e-3);
    const chTilesX = chTxEnd - chTxBase + 1;
    const chTilesY = chTyEnd - chTyBase + 1;
    if (chTilesX <= 0 || chTilesY <= 0) continue;
    if (tx < chTxBase || tx >= chTxBase + chTilesX) continue;
    if (ty < chTyBase || ty >= chTyBase + chTilesY) continue;
    const cs = c.cubeSize;
    const half = cs * 0.5;
    const bxTileMin = Math.floor((x0t - c.ox) / cs);
    const bxTileMax = Math.floor((x1t - 1e-4 - c.ox) / cs);
    const byTileMin = Math.floor((y0t - c.oy) / cs);
    const byTileMax = Math.floor((y1t - 1e-4 - c.oy) / cs);
    let bxMin = Math.floor((x - r - c.ox) / cs) - 1;
    if (bxTileMin > bxMin) bxMin = bxTileMin;
    let bxMax = Math.floor((x + r - c.ox) / cs) + 1;
    if (bxTileMax < bxMax) bxMax = bxTileMax;
    let byMin = Math.floor((y - r - c.oy) / cs) - 1;
    if (byTileMin > byMin) byMin = byTileMin;
    let byMax = Math.floor((y + r - c.oy) / cs) + 1;
    if (byTileMax < byMax) byMax = byTileMax;
    let bzMin = Math.floor((feetZ - c.oz) / cs) - 1;
    let bzMax = Math.floor((headZ - c.oz) / cs) + 1;
    if (bzMin < 0) bzMin = 0;
    if (bzMax >= c.sz) bzMax = c.sz - 1;
    if (bzMin > bzMax) continue;
    const sxsy = c.sx * c.sy;
    for (let bz = bzMin; bz <= bzMax; bz++) {
      const zBase = bz * sxsy;
      const zLo = c.oz + bz * cs;
      const zHi = zLo + cs;
      if (zHi <= feetZ || zLo >= headZ) continue;
      for (let by = byMin; by <= byMax; by++) {
        if (by < 0 || by >= c.sy) continue;
        const yBase = by * c.sx;
        for (let bx = bxMin; bx <= bxMax; bx++) {
          if (bx < 0 || bx >= c.sx) continue;
          const idx = bx + yBase + zBase;
          if ((c.alive[idx >> 3] & (1 << (idx & 7))) === 0) continue;
          const cx = c.ox + (bx + 0.5) * cs;
          const cy = c.oy + (by + 0.5) * cs;
          if (circleVsAABB(x, y, r, cx - half, cy - half, cx + half, cy + half)) {
            if (exemptCovers(ex, r, cx - half, cy - half, cx + half, cy + half)) continue;
            return true;
          }
        }
      }
    }
  }
  return false;
}

function circleVsAABB(cx: number, cy: number, r: number, x0: number, y0: number, x1: number, y1: number): boolean {
  let px = cx;
  if (px < x0) px = x0; else if (px > x1) px = x1;
  let py = cy;
  if (py < y0) py = y0; else if (py > y1) py = y1;
  const dx = cx - px;
  const dy = cy - py;
  return dx * dx + dy * dy <= r * r + CIRCLE_AABB_EPS_SQ;
}

function cylinderOverlapsVerticalAABB(
  cx: number, cy: number, feetZ: number, r: number, colliderH: number,
  minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number,
): boolean {
  const headZ = feetZ + colliderH;
  if (headZ <= minZ || feetZ >= maxZ) return false;
  return circleVsAABB(cx, cy, r, minX, minY, maxX, maxY);
}

/** Move-and-slide с voxel-aware collision + Doom-2-стиль лестниц.
 *  Сначала полная диагональ. Иначе скольжение: доминирующая ось первой
 *  (|dx| vs |dy|) — плавнее вдоль стен при стрейфе.
 *  Это убирает залипания на воксельных краях в проходах.
 *
 *  Поведение:
 *   - Стены/двери блокируют цилиндр; разрешён мгновенный step-up до STEP_UP_MAX
 *     (порог, маленький бугор) — Z выставляется в floor.
 *   - Лестницы (RoleStair) в столкновении цилиндра НЕ участвуют. Если
 *     поверхность под новой точкой не выше STAIR_STEP_UP_MAX над ступнями,
 *     шаг проходит «свободно» (stepUpZ = -1), а Z плавно «догоняется» через
 *     `floorAt` + `smoothFeetTowardFloor` в prediction.applyInput. Это
 *     делает подъём/спуск по лестницам гладким (как Doom 2) и убирает
 *     залипание на лестнице при ходьбе вниз по уклону.
 *
 *  Возвращает [newX, newY, stepUpZ]. stepUpZ = -1 если мгновенного подъёма
 *  не было (включая всё движение по лестницам). */
export function moveAndSlideVoxel(
  map: GridMap,
  chunks: ChunkRegistry,
  damaged: Set<number>,
  x: number, y: number, z: number, height: number,
  dx: number, dy: number, r: number,
): [number, number, number] {
  if (dx === 0 && dy === 0) return [x, y, -1];
  const stepUp = (tx: number, ty: number): [boolean, number] => {
    if (collidesDecor(chunks, tx, ty, z, r, height)) return [false, -1];
    if (playerCollidesWalls(map, chunks, damaged, tx, ty, z, r, height)) {
      const floor = floorAt(chunks, tx, ty, r, z + STEP_UP_MAX + 0.01);
      if (floor <= z + 0.04) return [false, -1];
      if (floor - z > STEP_UP_MAX) return [false, -1];
      // На новой высоте цилиндр обязан быть свободен (зеркало сервера):
      // иначе step-up вдавит макушку в нависающую геометрию.
      if (playerCollidesWalls(map, chunks, damaged, tx, ty, floor, r, height)) return [false, -1];
      return [true, floor];
    }
    // Стены/двери чисты. Лестница (если есть) пропускается без Z-скачка,
    // если её поверхность над новой точкой не выше STAIR_STEP_UP_MAX.
    // Гейт достижимости (зеркало сервера): марш ВЫШЕ головы — потолок,
    // а не стена; шаг под ним свободен (мансардная лестница над коридором).
    const stairTop = stairSurfaceTopAt(chunks, tx, ty, r);
    if (stairTop > z + STAIR_STEP_UP_MAX && stairTop <= z + height) return [false, -1];
    return [true, -1];
  };
  // «Уже внутри» (зеркало server MovePlayerVoxelZ): цилиндр задевает геометрию
  // в ТЕКУЩЕЙ точке — упал сверху в бокс мебели (глобус библиотеки), встал из
  // приседа. stepUp отказывает в любом шаге, после которого что-то ещё задето,
  // и из глобуса 1.3 м за тик не выйти — заперт навсегда. Разрешаем шаг, не
  // задевающий ничего НОВОГО (OverlapExempt). Проверка ленивая — после отказа
  // обычного шага, обычные шаги идут прежним путём.
  let stuckKnown = false;
  let exempt: OverlapExempt | undefined;
  const tryStep = (tx: number, ty: number): [boolean, number] => {
    const res = stepUp(tx, ty);
    if (res[0]) return res;
    if (!stuckKnown) {
      stuckKnown = true;
      if (
        collidesDecor(chunks, x, y, z, r, height) ||
        playerCollidesWalls(map, chunks, damaged, x, y, z, r, height)
      ) {
        exempt = { x, y };
      }
    }
    if (!exempt) return [false, -1];
    if (collidesDecor(chunks, tx, ty, z, r, height, exempt)) return [false, -1];
    if (playerCollidesWalls(map, chunks, damaged, tx, ty, z, r, height, exempt)) return [false, -1];
    const stairTop = stairSurfaceTopAt(chunks, tx, ty, r);
    if (stairTop > z + STAIR_STEP_UP_MAX && stairTop <= z + height) return [false, -1];
    return [true, -1];
  };
  {
    const [ok, nz] = tryStep(x + dx, y + dy);
    if (ok) return [x + dx, y + dy, nz];
  }
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  if (adx >= ady) {
    let nx = x;
    let zUp = -1;
    if (dx !== 0) {
      const [ok, nz] = tryStep(x + dx, y);
      if (ok) {
        nx = x + dx;
        zUp = nz;
      }
    }
    let ny = y;
    if (dy !== 0) {
      const [ok, nz] = tryStep(nx, y + dy);
      if (ok) {
        ny = y + dy;
        if (nz > zUp) zUp = nz;
      }
    }
    return [nx, ny, zUp];
  }
  let ny = y;
  let zUp = -1;
  if (dy !== 0) {
    const [ok, nz] = tryStep(x, y + dy);
    if (ok) {
      ny = y + dy;
      zUp = nz;
    }
  }
  let nx = x;
  if (dx !== 0) {
    const [ok, nz] = tryStep(x + dx, ny);
    if (ok) {
      nx = x + dx;
      if (nz > zUp) zUp = nz;
    }
  }
  return [nx, ny, zUp];
}

/** Является ли чанк статической декорацией (не стенами/полом/потолком,
 *  не dynamic rigid-body) — то, на что игрок может наступать и обо что
 *  упирается горизонтально. Отбор по role (в loaded-уровнях у стен/пола/
 *  потолка всегда есть текстура, поэтому старый textureName-фильтр ломался). */
function isStaticDecor(c: VoxelChunkClient): boolean {
  if (c.dynamic) return false;
  return c.role === ChunkRole.Decor;
}

/** Горизонтальная коллизия игрока (cx,cy,cz,r,height) со всеми статическими
 *  декорациями. Игрок проходит над декорацией, если его ступни выше её
 *  крыши (cz >= MaxZ), и под ней — если макушка ниже её пола. */
export function collidesDecor(
  chunks: ChunkRegistry, cx: number, cy: number, cz: number, r: number, height: number,
  /** «Уже внутри»: предмет, задетый в ex, не блокирует (зеркало server collidesDecorEx). */
  ex?: OverlapExempt,
): boolean {
  const headZ = cz + height;
  for (const c of chunks.all()) {
    if (!isStaticDecor(c)) continue;
    const minX = c.ox;
    const minY = c.oy;
    const minZ = c.oz;
    const maxX = c.ox + c.sx * c.cubeSize;
    const maxY = c.oy + c.sy * c.cubeSize;
    const maxZ = c.oz + c.sz * c.cubeSize;
    if (cz >= maxZ) continue;
    if (headZ <= minZ) continue;
    if (!circleVsAABB(cx, cy, r, minX, minY, maxX, maxY)) continue;
    // Bbox — только претест. Сборные decor-чанки level-pack'а (все фонари
    // сада/дома в ОДНОМ чанке, bbox 63×51 м с z-полосой через весь первый
    // этаж) блокировали предикцию по всему дому, при том что сервер после
    // зеркального фикса (decorColumnsHitCylinder) пускает: клиент «стоп»,
    // сервер «иди» — персонаж дёргался при честных 120 fps. Уточняем по
    // живым кубам — блокирует сам столб фонаря, а не общий габарит.
    if (voxelCylinderHitsChunk(c, cx, cy, cz, r, height)) {
      // Предмет целиком: из глубины сплошного кресла любой шаг задевает новые
      // кубы, покубовое правило запирало бы так же.
      if (ex && voxelCylinderHitsChunk(c, ex.x, ex.y, cz, r, height)) continue;
      return true;
    }
  }
  return false;
}

/** Какие роли попадают в voxel-column поиск floor (wall/floor/terrain/stair). */
function isFloorSource(c: VoxelChunkClient): boolean {
  if (c.dynamic) return false;
  return (
    c.role === ChunkRole.Wall ||
    c.role === ChunkRole.Floor ||
    c.role === ChunkRole.Terrain ||
    c.role === ChunkRole.Stair
  );
}

/** Ceiling: role=Ceiling или Terrain (пещерные своды). */
function isCeilingSource(c: VoxelChunkClient): boolean {
  if (c.dynamic) return false;
  return c.role === ChunkRole.Ceiling || c.role === ChunkRole.Terrain;
}

/** Одна колонка под (cx,cy) для stair — зеркало server chunkColumnCenterTop. */
function chunkColumnCenterTop(
  c: VoxelChunkClient, cx: number, cy: number, maxZ: number,
): number {
  ensureColumnMaps(c);
  if (c.columnTopZ === null) return COLUMN_EMPTY_TOP;
  const cs = c.cubeSize;
  const sx = c.sx;
  const sy = c.sy;
  const bx = Math.floor((cx - c.ox) / cs);
  const by = Math.floor((cy - c.oy) / cs);
  if (bx < 0 || by < 0 || bx >= sx || by >= sy) return COLUMN_EMPTY_TOP;
  const v = c.columnTopZ[bx + by * sx];
  if (v > maxZ) return COLUMN_EMPTY_TOP;
  return v;
}

/** Соседние столбцы для terrain (дыры в column map). */
function terrainNeighborColumnTop(
  col: Float32Array, sx: number, sy: number, bx: number, by: number,
): number {
  const rings = 2;
  let best = COLUMN_EMPTY_TOP;
  for (let dy = -rings; dy <= rings; dy++) {
    for (let dx = -rings; dx <= rings; dx++) {
      if (dx === 0 && dy === 0) continue;
      const nx = bx + dx;
      const ny = by + dy;
      if (nx < 0 || ny < 0 || nx >= sx || ny >= sy) continue;
      const t = col[nx + ny * sx];
      if (t !== COLUMN_EMPTY_TOP && t > best) best = t;
    }
  }
  return best;
}

/** Max ColumnTopZ в окне радиуса — зеркало server chunkColumnMax. */
function chunkColumnTopMax(
  c: VoxelChunkClient,
  cx: number, cy: number, r: number, maxZ: number,
): number {
  ensureColumnMaps(c);
  if (c.columnTopZ === null) return COLUMN_EMPTY_TOP;
  const cs = c.cubeSize;
  const sx = c.sx;
  const sy = c.sy;
  let bxMin = Math.floor((cx - r - c.ox) / cs);
  let bxMax = Math.floor((cx + r - c.ox) / cs);
  let byMin = Math.floor((cy - r - c.oy) / cs);
  let byMax = Math.floor((cy + r - c.oy) / cs);
  if (bxMin < 0) bxMin = 0;
  if (byMin < 0) byMin = 0;
  if (bxMax >= sx) bxMax = sx - 1;
  if (byMax >= sy) byMax = sy - 1;
  if (bxMin > bxMax || byMin > byMax) return COLUMN_EMPTY_TOP;
  const r2 = r * r;
  let best = COLUMN_EMPTY_TOP;
  for (let by = byMin; by <= byMax; by++) {
    const cellCy = c.oy + (by + 0.5) * cs;
    for (let bx = bxMin; bx <= bxMax; bx++) {
      const cellCx = c.ox + (bx + 0.5) * cs;
      const dx = cx - clamp(cx, cellCx - cs * 0.5, cellCx + cs * 0.5);
      const dy = cy - clamp(cy, cellCy - cs * 0.5, cellCy + cs * 0.5);
      if (dx * dx + dy * dy >= r2) continue;
      let v = c.columnTopZ[bx + by * sx];
      if (v === COLUMN_EMPTY_TOP && c.role === ChunkRole.Terrain) {
        v = terrainNeighborColumnTop(c.columnTopZ, sx, sy, bx, by);
      }
      if (v === COLUMN_EMPTY_TOP) continue;
      if (v > maxZ) continue;
      if (v > best) best = v;
    }
  }
  return best;
}

/** Column-lookup ceiling: min ColumnBottomZ > minZ. */
function chunkColumnBottomMin(
  c: VoxelChunkClient,
  cx: number, cy: number, r: number, minZ: number,
): number {
  ensureColumnMaps(c);
  if (c.columnBottomZ === null) return Infinity;
  const cs = c.cubeSize;
  const sx = c.sx;
  const sy = c.sy;
  let bxMin = Math.floor((cx - r - c.ox) / cs);
  let bxMax = Math.floor((cx + r - c.ox) / cs);
  let byMin = Math.floor((cy - r - c.oy) / cs);
  let byMax = Math.floor((cy + r - c.oy) / cs);
  if (bxMin < 0) bxMin = 0;
  if (byMin < 0) byMin = 0;
  if (bxMax >= sx) bxMax = sx - 1;
  if (byMax >= sy) byMax = sy - 1;
  if (bxMin > bxMax || byMin > byMax) return Infinity;
  const r2 = r * r;
  let best = Infinity;
  for (let by = byMin; by <= byMax; by++) {
    const cellCy = c.oy + (by + 0.5) * cs;
    for (let bx = bxMin; bx <= bxMax; bx++) {
      const cellCx = c.ox + (bx + 0.5) * cs;
      const dx = cx - clamp(cx, cellCx - cs * 0.5, cellCx + cs * 0.5);
      const dy = cy - clamp(cy, cellCy - cs * 0.5, cellCy + cs * 0.5);
      if (dx * dx + dy * dy >= r2) continue;
      const v = c.columnBottomZ[bx + by * sx];
      if (v === COLUMN_EMPTY_BOTTOM) continue;
      if (v <= minZ) continue;
      if (v < best) best = v;
    }
  }
  return best;
}

function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

/** Зеркало серверного гейта: максимальная ДОСТИЖИМАЯ ступень mesh-лестниц
 *  под точкой (-Infinity, если такой нет). Лестница «глушит» полы под собой
 *  только там, где её ступень достижима (не выше зоны поиска опоры), и только
 *  полы, которые марш ПРОНЗАЕТ (не выше ступени + шага): пол этажом выше
 *  марша — самостоятельная опора, игрок ходит по нему НАД лестницей. */
function reachableStairSupport(
  stairUnder: number[], cx: number, cy: number, r: number, maxFloorZ: number,
): number {
  if (!meshCollisionBoxes) return -Infinity;
  let best = -Infinity;
  for (const si of stairUnder) {
    const sz = stairMeshFootSupportZ(cx, cy, r, meshCollisionBoxes[si], si);
    if (sz <= maxFloorZ + 0.01 && sz > best) best = sz;
  }
  return best;
}

/** Column-lookup floor (без decor AABB). Зеркало server `floorAtColumn`:
 *  0, если опоры нет (контракт для обломков и краёв карты). */
export function floorAtColumn(
  chunks: ChunkRegistry, cx: number, cy: number, r: number, maxFloorZ: number,
): number {
  const z = floorAtColumnRaw(chunks, cx, cy, r, maxFloorZ);
  return z > COLUMN_EMPTY_TOP ? z : 0;
}

/** Зеркало server `floorAtColumnFound`: поиск стартует с «нет опоры», а не с
 *  нуля, иначе полы ниже нуля (тоннель z=−1) проигрывали нулю. */
function floorAtColumnRaw(
  chunks: ChunkRegistry, cx: number, cy: number, r: number, maxFloorZ: number,
): number {
  const stairUnder = meshCollisionBoxes ? meshStairIndicesUnderPoint(cx, cy) : [];
  const stairSupport = reachableStairSupport(stairUnder, cx, cy, r, maxFloorZ);
  let best = COLUMN_EMPTY_TOP;
  for (const c of chunks.all()) {
    if (!isFloorSource(c)) continue;
    const v =
      c.role === ChunkRole.Stair
        ? chunkColumnCenterTop(c, cx, cy, maxFloorZ)
        : chunkColumnTopMax(c, cx, cy, r, maxFloorZ);
    // Глушим только пол, который марш пронзает (зеркало сервера).
    if (
      stairSupport > -Infinity &&
      (c.role === ChunkRole.Floor || c.role === ChunkRole.Terrain) &&
      v <= stairSupport + STAIR_STEP_UP_MAX
    ) {
      continue;
    }
    if (v > best) best = v;
  }
  return best;
}

/** Column-lookup ceiling. */
export function ceilingAtColumn(
  chunks: ChunkRegistry, cx: number, cy: number, r: number, minCeilZ: number,
): number {
  let best = Infinity;
  for (const c of chunks.all()) {
    if (!isCeilingSource(c)) continue;
    const v = chunkColumnBottomMin(c, cx, cy, r, minCeilZ);
    if (v < best) best = v;
  }
  return best;
}

function meshCollisionStairSurfacePlanar(sx: number, sy: number, box: MeshCollisionEntry): number {
  const minX = box.min[0];
  const minY = box.min[1];
  const minZ = box.min[2];
  const maxX = box.max[0];
  const maxY = box.max[1];
  const maxZ = box.max[2];
  const dx = maxX - minX;
  const dy = maxY - minY;
  const dz = maxZ - minZ;
  if (dz <= 1e-5) return maxZ;
  const sc = box.stair_climb_xy;
  if (sc && sc.length === 2) {
    const vx = sc[0];
    const vy = sc[1];
    if (vx * vx + vy * vy > 1e-6) {
      const corners: [number, number][] = [
        [minX, minY], [maxX, minY], [minX, maxY], [maxX, maxY],
      ];
      let umin = Infinity;
      let umax = -Infinity;
      for (const [qx, qy] of corners) {
        const d = (qx - minX) * vx + (qy - minY) * vy;
        if (d < umin) umin = d;
        if (d > umax) umax = d;
      }
      const span = umax - umin;
      if (span > 1e-5) {
        let u = ((sx - minX) * vx + (sy - minY) * vy - umin) / span;
        if (u < 0) u = 0;
        else if (u > 1) u = 1;
        return minZ + u * dz;
      }
    }
  }
  let ux: number;
  if (dx <= 1e-6) ux = 0;
  else ux = Math.max(0, Math.min(1, (sx - minX) / dx));
  let uy: number;
  if (dy <= 1e-6) uy = 0;
  else uy = Math.max(0, Math.min(1, (sy - minY) / dy));
  const long = Math.max(dx, dy);
  const short = Math.min(dx, dy);
  const aspect = long > 1e-6 ? short / long : 1;
  const aspectUseMax = 0.48;
  if (aspect > aspectUseMax) {
    const u = Math.max(ux, uy);
    return minZ + u * dz;
  }
  if (dx >= dy) return minZ + ux * dz;
  return minZ + uy * dz;
}

/** Одна вертикаль под (cx,cy) для mesh stair — зеркало server stairMeshFootSupportZ.
 *  boxIdx нужен для сэмпла stair_tris (Float32Array лежит в параллельном кэше). */
function stairMeshFootSupportZ(
  cx: number, cy: number, _r: number, box: MeshCollisionEntry, boxIdx: number,
): number {
  let px = cx;
  if (px < box.min[0]) px = box.min[0];
  else if (px > box.max[0]) px = box.max[0];
  let py = cy;
  if (py < box.min[1]) py = box.min[1];
  else if (py > box.max[1]) py = box.max[1];
  return meshCollisionStairSurfaceZ(px, py, box, boxIdx);
}

function meshCollisionStairSurfaceZ(
  sx: number, sy: number, box: MeshCollisionEntry, boxIdx: number,
): number {
  // 1. Raycast по треугольникам (точная Z = верх видимого меша).
  if (stairTrisDecoded && boxIdx >= 0) {
    const mt = stairTrisDecoded[boxIdx];
    if (mt) {
      const z = sampleStairTris(sx, sy, mt);
      if (z !== null) return z;
    }
  }
  // 2. Heightfield (билиней по узлам).
  const hf = box.stair_heightfield;
  if (hf) {
    const z = sampleStairMeshHF(sx, sy, hf);
    if (z !== null) return z;
  }
  // 3. Планарный пандус.
  return meshCollisionStairSurfacePlanar(sx, sy, box);
}

function sampleStairTris(cx: number, cy: number, mt: StairTrisDecoded): number | null {
  if (mt.nx <= 0 || mt.ny <= 0 || mt.cell <= 1e-8) return null;
  const ix = Math.floor((cx - mt.ox) / mt.cell);
  const iy = Math.floor((cy - mt.oy) / mt.cell);
  if (ix < -1 || iy < -1 || ix > mt.nx || iy > mt.ny) return null;
  let best = -Infinity;
  let hit = false;
  for (let dj = -1; dj <= 1; dj++) {
    const jy = iy + dj;
    if (jy < 0 || jy >= mt.ny) continue;
    for (let di = -1; di <= 1; di++) {
      const jx = ix + di;
      if (jx < 0 || jx >= mt.nx) continue;
      const cellIdx = jy * mt.nx + jx;
      const start = mt.binOffsets[cellIdx];
      const end = mt.binOffsets[cellIdx + 1];
      for (let k = start; k < end; k++) {
        const ti = mt.binIndices[k] * 9;
        const ax = mt.verts[ti + 0], ay = mt.verts[ti + 1], az = mt.verts[ti + 2];
        const bx = mt.verts[ti + 3], by = mt.verts[ti + 4], bz = mt.verts[ti + 5];
        const cxv = mt.verts[ti + 6], cyv = mt.verts[ti + 7], czv = mt.verts[ti + 8];
        if (!pointInTri2D(cx, cy, ax, ay, bx, by, cxv, cyv)) continue;
        const z = zOnUpperTriPlane3(cx, cy, ax, ay, az, bx, by, bz, cxv, cyv, czv);
        if (z !== null && z > best) {
          best = z;
          hit = true;
        }
      }
    }
  }
  return hit ? best : null;
}

function pointInTri2D(
  px: number, py: number, ax: number, ay: number,
  bx: number, by: number, cx: number, cy: number,
): boolean {
  const s1 = (ay - py) * (bx - ax) - (ax - px) * (by - ay);
  const s2 = (by - py) * (cx - bx) - (bx - px) * (cy - by);
  const s3 = (cy - py) * (ax - cx) - (cx - px) * (ay - cy);
  const hasNeg = s1 < 0 || s2 < 0 || s3 < 0;
  const hasPos = s1 > 0 || s2 > 0 || s3 > 0;
  return !(hasNeg && hasPos);
}

function zOnUpperTriPlane3(
  px: number, py: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): number | null {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  let nx = aby * acz - abz * acy;
  let ny = abz * acx - abx * acz;
  let nz = abx * acy - aby * acx;
  const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (l < 1e-8) return null;
  nx /= l; ny /= l; nz /= l;
  if (nz < 0) { nx = -nx; ny = -ny; nz = -nz; }
  if (nz < 1e-3) return null;
  return az - (nx * (px - ax) + ny * (py - ay)) / nz;
}

function sampleStairMeshHF(
  sx: number, sy: number,
  hf: NonNullable<MeshCollisionEntry["stair_heightfield"]>,
): number | null {
  const STAIR_MESH_HF_SENTINEL = -1e15;
  const nx = hf.nx;
  const ny = hf.ny;
  const cell = hf.cell;
  if (nx < 1 || ny < 1 || cell <= 1e-8) return null;
  if (!hf.z || hf.z.length !== nx * ny) return null;
  let u = (sx - hf.ox) / cell;
  let v = (sy - hf.oy) / cell;
  if (u < 0) u = 0;
  else if (u > nx - 1) u = nx - 1;
  if (v < 0) v = 0;
  else if (v > ny - 1) v = ny - 1;
  let ix0 = Math.floor(u);
  let iy0 = Math.floor(v);
  if (ix0 >= nx) ix0 = nx - 1;
  if (iy0 >= ny) iy0 = ny - 1;
  let fu = u - ix0;
  let fv = v - iy0;
  let ix1 = ix0 + 1;
  if (ix1 >= nx) {
    ix1 = nx - 1;
    fu = 0;
  }
  let iy1 = iy0 + 1;
  if (iy1 >= ny) {
    iy1 = ny - 1;
    fv = 0;
  }
  const z00 = hf.z[iy0 * nx + ix0];
  const z10 = hf.z[iy0 * nx + ix1];
  const z01 = hf.z[iy1 * nx + ix0];
  const z11 = hf.z[iy1 * nx + ix1];
  if (
    z00 > STAIR_MESH_HF_SENTINEL &&
    z10 > STAIR_MESH_HF_SENTINEL &&
    z01 > STAIR_MESH_HF_SENTINEL &&
    z11 > STAIR_MESH_HF_SENTINEL
  ) {
    return (1 - fu) * (1 - fv) * z00 + fu * (1 - fv) * z10 + (1 - fu) * fv * z01 + fu * fv * z11;
  }
  let ix = Math.floor((sx - hf.ox) / cell + 1e-6);
  let iy = Math.floor((sy - hf.oy) / cell + 1e-6);
  if (ix < 0) ix = 0;
  else if (ix >= nx) ix = nx - 1;
  if (iy < 0) iy = 0;
  else if (iy >= ny) iy = ny - 1;
  const z = hf.z[iy * nx + ix];
  if (z > STAIR_MESH_HF_SENTINEL) return z;
  const ring = 2;
  let best = -2e20;
  for (let dj = -ring; dj <= ring; dj++) {
    const jy = iy + dj;
    if (jy < 0 || jy >= ny) continue;
    const row = jy * nx;
    for (let di = -ring; di <= ring; di++) {
      const jx = ix + di;
      if (jx < 0 || jx >= nx) continue;
      const zz = hf.z[row + jx];
      if (zz > STAIR_MESH_HF_SENTINEL && zz > best) best = zz;
    }
  }
  if (best > STAIR_MESH_HF_SENTINEL) return best;
  return null;
}

function meshAABBOverlap2D(a: MeshCollisionEntry, b: MeshCollisionEntry): boolean {
  return !(
    a.max[0] < b.min[0] ||
    b.max[0] < a.min[0] ||
    a.max[1] < b.min[1] ||
    b.max[1] < a.min[1]
  );
}

function meshStairIndicesUnderPoint(cx: number, cy: number): number[] {
  if (!meshCollisionBoxes) return [];
  const out: number[] = [];
  for (let i = 0; i < meshCollisionBoxes.length; i++) {
    const b = meshCollisionBoxes[i];
    if (b.role !== "stair") continue;
    if (cx < b.min[0] || cy < b.min[1] || cx > b.max[0] || cy > b.max[1]) continue;
    out.push(i);
  }
  return out;
}

/** Высота опоры под игроком в (cx,cy). Макс из column-lookup, decor-AABB,
 *  terrain HM (если центр не в XY mesh stair) и mesh_collision. На mesh stair
 *  terrain HM и mesh/voxel floor+terrain не смешиваются с тредами. */
export function floorAt(
  chunks: ChunkRegistry, cx: number, cy: number, r: number, maxFloorZ: number,
): number {
  let floor = floorAtColumnRaw(chunks, cx, cy, r, maxFloorZ);
  for (const c of chunks.all()) {
    if (!isStaticDecor(c)) continue;
    const minX = c.ox;
    const minY = c.oy;
    const minZ = c.oz;
    const maxX = c.ox + c.sx * c.cubeSize;
    const maxY = c.oy + c.sy * c.cubeSize;
    if (minZ > maxFloorZ) continue;
    if (circleVsAABB(cx, cy, r, minX, minY, maxX, maxY)) {
      // Per-column, не по AABB (зеркало сервера): фонари дома слиты в один
      // чанк на пол-дома — верх его бокса давал «стеклянный мост» над залом.
      const top = chunkColumnTopMax(c, cx, cy, r, maxFloorZ);
      if (top > floor) floor = top;
    }
  }
  const stairUnder = meshCollisionBoxes ? meshStairIndicesUnderPoint(cx, cy) : [];
  const hm = getTerrainHeightmap();
  if (hm) {
    let t = terrainFloorAt(hm, cx, cy, r, maxFloorZ);
    if (t !== TERRAIN_EMPTY_TOP) {
      for (const si of stairUnder) {
        const sb = meshCollisionBoxes![si];
        // Марш глубоко под поверхностью не глушит землю над собой (зеркало
        // сервера: подземный тоннель против площади сверху).
        if (sb.max[2] + STAIR_STEP_UP_MAX < t) continue;
        const sz = stairMeshFootSupportZ(cx, cy, r, sb, si);
        if (t > sz + MESH_FLOOR_OVER_MESH_STAIR_SKIP_DZ) {
          t = TERRAIN_EMPTY_TOP;
          break;
        }
      }
      if (t !== TERRAIN_EMPTY_TOP && t > floor) floor = t;
    }
  }
  if (meshCollisionBoxes) {
    // Бинарного гейта «на лестнице → глушим все mesh-полы» здесь больше нет
    // (зеркало сервера): он ронял игрока с пола этажом выше марша. Конфликт
    // mesh-пола с лестницей разруливает дельта-правило ниже
    // (MESH_FLOOR_OVER_MESH_STAIR_SKIP_DZ).
    meshLoop:
    for (let i = 0; i < meshCollisionBoxes.length; i++) {
      const box = meshCollisionBoxes[i];
      const role = box.role;
      if (role !== "floor" && role !== "terrain" && role !== "stair") continue;
      const minX = box.min[0];
      const minY = box.min[1];
      const maxX = box.max[0];
      const maxY = box.max[1];
      let supportZ: number;
      if (role === "stair") {
        // Одна вертикаль в (cx,cy): как server stairSurfaceTopAt / stairMeshFootSupportZ.
        if (cx < minX || cy < minY || cx > maxX || cy > maxY) continue;
        supportZ = stairMeshFootSupportZ(cx, cy, r, box, i);
      } else if (role === "floor" && box.stair_heightfield) {
        // Floor с heightfield (multi-Z mesh типа podium-fundament):
        // семплируем HF как для stair'а. Без этого AABB.MaxZ применяется
        // везде внутри AABB → 0.5м обрыв на границе с stair-областью
        // (для floor_manison_fundament: MaxZ=6.88 vs stair top 6.37 → drop
        // в один тик при выходе center'а за fund.AABB = «бамп»).
        let px = cx;
        if (px < minX) px = minX;
        else if (px > maxX) px = maxX;
        let py = cy;
        if (py < minY) py = minY;
        else if (py > maxY) py = maxY;
        const dxc = cx - px;
        const dyc = cy - py;
        if (dxc * dxc + dyc * dyc >= r * r) continue;
        supportZ = stairMeshFootSupportZ(cx, cy, r, box, i);
      } else {
        // Floor (single-Z) / Terrain: CENTER-based. Disc-based давал «прыжок Z»
        // на стыке «лестница → плоский пол» (когда диск ноги касается AABB-пола
        // за r=0.18 м до реальной кромки и supportZ перескакивает с stair-HF на
        // floor.MaxZ). См. server/internal/game/sim.go (тот же фикс).
        if (cx < minX || cx > maxX || cy < minY || cy > maxY) continue;
        supportZ = box.max[2];
      }
      if (role === "floor" || role === "terrain") {
        for (const si of stairUnder) {
          const sb = meshCollisionBoxes[si];
          if (!meshAABBOverlap2D(box, sb)) continue;
          // Марш целиком глубоко ПОД полом не глушит его: глушение — про
          // «пол, сквозь который проходит лестница». Подземный тоннельный марш
          // ронял игрока сквозь плиту площади в 4 м над ним (зеркало сервера).
          if (sb.max[2] + STAIR_STEP_UP_MAX < box.min[2]) continue;
          const sz = stairMeshFootSupportZ(cx, cy, r, sb, si);
          if (supportZ > sz + MESH_FLOOR_OVER_MESH_STAIR_SKIP_DZ) {
            // Sliced-ячейка пола существует только там, где пол геометрически
            // есть — этаж выше марша больше чем на шаг не глушим (зеркало сервера).
            if (box.sliced && supportZ > sz + STAIR_STEP_UP_MAX) continue;
            continue meshLoop;
          }
        }
      }
      if (supportZ <= floor || supportZ > maxFloorZ) continue;
      if (supportZ > floor) floor = supportZ;
    }
  }
  // Зеркало server floorAt: 0, если опоры нет вовсе.
  return floor > COLUMN_EMPTY_TOP ? floor : 0;
}

/** Нижняя грань ближайшей декорации НАД игроком. +Infinity если сверху
 *  ничего нет. Объединяет column-lookup (ceiling/terrain) и decor-AABB. */
export function ceilingAt(
  chunks: ChunkRegistry, cx: number, cy: number, r: number, minCeilZ: number,
): number {
  let ceil = ceilingAtColumn(chunks, cx, cy, r, minCeilZ);
  for (const c of chunks.all()) {
    if (!isStaticDecor(c)) continue;
    const minX = c.ox;
    const minY = c.oy;
    const minZ = c.oz;
    const maxX = c.ox + c.sx * c.cubeSize;
    const maxY = c.oy + c.sy * c.cubeSize;
    if (minZ <= minCeilZ || minZ >= ceil) continue;
    if (circleVsAABB(cx, cy, r, minX, minY, maxX, maxY)) {
      if (minZ < ceil) ceil = minZ;
    }
  }
  return ceil;
}

/** STEP_UP_MAX / STAIR_STEP_UP_MAX — зеркало server player.go (StepUpMax / StairStepUpMax).
 *  moveAndSlideVoxel и prediction используют оба. */
export const STEP_UP_MAX = 0.25;
/** При упоре в stair_* — выше порог, чем у стен (Doom-style). */
export const STAIR_STEP_UP_MAX = 0.9;
/** Зеркало server MeshFloorOverMeshStairSkipDz — floorAt: mesh floor не давит stair. */
export const MESH_FLOOR_OVER_MESH_STAIR_SKIP_DZ = 0.32;

/** Помечаем tile damaged когда cube был сбит в wall chunk. */
export function markTileDamaged(
  chunk: VoxelChunkClient,
  cubeIdx: number,
  map: GridMap,
  damaged: Set<number>,
): void {
  if (chunk.role !== ChunkRole.Wall) return;
  const bx = cubeIdx % chunk.sx;
  const by = Math.floor(cubeIdx / chunk.sx) % chunk.sy;
  const bz = Math.floor(cubeIdx / (chunk.sx * chunk.sy));
  const maxBzPlayer = Math.floor(PLAYER_COLLIDER_HEIGHT / chunk.cubeSize) + 1;
  if (bz > maxBzPlayer) return;
  const cwx = chunk.ox + (bx + 0.5) * chunk.cubeSize;
  const cwy = chunk.oy + (by + 0.5) * chunk.cubeSize;
  const [tx, ty] = worldToCell(map, cwx, cwy);
  damaged.add(tx + ty * map.width);
}
